import dayjs from "dayjs";
import * as yup from "yup";
import {
  BROADCAST_BODY_MAX,
  BROADCAST_TITLE_MAX
} from "../esim/broadcastStatus";

const BroadcastNotificationSchema = yup.object().shape({
  name: yup.string().trim().required("Emri i njoftimit është i detyrueshëm"),
  sendMode: yup.string().oneOf(["now", "schedule"]).required(),
  scheduledAt: yup.string().when("sendMode", {
    is: "schedule",
    then: (schema) =>
      schema
        .required("Zgjidhni datën dhe orën e dërgimit")
        .test(
          "is-future",
          "Koha e dërgimit duhet të jetë të paktën 2 minuta në të ardhmen",
          // dayjs.tz matches how the value is interpreted on submit (the app's
          // display timezone), and the 2-minute floor mirrors the backend rule.
          (value) =>
            !!value && dayjs.tz(value).valueOf() > Date.now() + 2 * 60 * 1000
        ),
    otherwise: (schema) => schema.notRequired()
  }),
  minAppVersion: yup
    .string()
    .trim()
    .notRequired()
    .test(
      "is-version",
      "Versioni duhet të jetë në formatin 1.2.0",
      (value) => !value || /^\d+(\.\d+){1,3}$/.test(value.trim())
    ),
  audienceMode: yup.string().oneOf(["all", "users"]).required(),
  targetUsers: yup.array().when("audienceMode", {
    is: "users",
    then: (schema) => schema.min(1, "Zgjidhni të paktën një përdorues"),
    otherwise: (schema) => schema.notRequired()
  }),
  // Only meaningful for "all" — in "users" mode the picker is hidden and the
  // submit sends an empty list, so it can never clash with targetUsers.
  excludedUsers: yup.array().notRequired(),
  routeChoice: yup.string().notRequired(),
  customRoute: yup.string().when("routeChoice", {
    is: "custom",
    then: (schema) =>
      schema
        .required("Shkruani rrugën e faqes")
        .test(
          "starts-with-slash",
          "Rruga duhet të fillojë me '/', p.sh. /my-esims",
          (value) => !!value && value.trim().startsWith("/")
        ),
    otherwise: (schema) => schema.notRequired()
  }),
  // The backend columns are nvarchar(200)/nvarchar(2000), and a translated string is routinely
  // longer than its source — German and Polish run well past Albanian. Without these the only
  // thing that catches an overflow is a backend 400 on save that does not say which language.
  defaultTitle: yup
    .string()
    .trim()
    .max(BROADCAST_TITLE_MAX, `Titulli nuk mund të kalojë ${BROADCAST_TITLE_MAX} karaktere`)
    .required("Titulli është i detyrueshëm"),
  defaultBody: yup
    .string()
    .trim()
    .max(BROADCAST_BODY_MAX, `Përmbajtja nuk mund të kalojë ${BROADCAST_BODY_MAX} karaktere`)
    .required("Përmbajtja është e detyrueshme"),
  translations: yup
    .array()
    .of(
      yup.object().shape({
        applicationLanguage: yup.string().required("Zgjidhni gjuhën"),
        title: yup
          .string()
          .trim()
          .max(BROADCAST_TITLE_MAX, `Titulli nuk mund të kalojë ${BROADCAST_TITLE_MAX} karaktere`)
          .required("Titulli është i detyrueshëm"),
        body: yup
          .string()
          .trim()
          .max(BROADCAST_BODY_MAX, `Përmbajtja nuk mund të kalojë ${BROADCAST_BODY_MAX} karaktere`)
          .required("Përmbajtja është e detyrueshme")
      })
    )
    // Duplicates used to be prevented only by the disabled attribute on the language select — a
    // UI convention with no data-level backstop. Rows are now created programmatically from a
    // network response, so that argument no longer holds; the backend rejects duplicates outright.
    .test(
      "unique-language",
      "Dy përkthime nuk mund të kenë të njëjtën gjuhë",
      (value) => {
        const codes = ((value || []) as { applicationLanguage?: string }[])
          .map((row) => row.applicationLanguage)
          .filter(Boolean);
        return new Set(codes).size === codes.length;
      }
    )
});

export default BroadcastNotificationSchema;
